import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { RouteComponentProps } from 'react-router-dom';
import { Card, Result, message } from 'antd';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import {
  useThemeConfig,
  useThemeLabel
} from 'client/components/schema/config/ClientConfigModule';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { CitizenApi } from 'client/components/schema/citizen/CitizenApi';
import {
  citizenRegisterValidator,
  ICitizenRegisterDto
} from 'common/dto/CitizenDto';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { logger } from 'client/core/logger/logger';
import { useTranslation } from 'react-i18next';
import { UserRegistrationForm } from 'client/components/schema/citizen/form/UserRegistrationForm';

interface Params {
  id: string;
}

export interface UserRegistrationPageProps
  extends RouteComponentProps<Params> {}

/**
 * Pagina per la registrazione di un utente con email e password (senza SPID)
 */
export function UserRegistrationPage(props: UserRegistrationPageProps) {
  const { t } = useTranslation();

  const privacyUrl = useThemeLabel('privacyUrl');
  const config = useThemeConfig();
  const tPrefix = config.labels.themeLocalPrefix;

  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('user.registration.breadcrumb', 'Registrazione Utente')
  });

  const [register, registerState] = useApiMutation(CitizenApi.register, {});

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  return (
    <FrontLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {/* Messaggio al successo dell'invio che sostituisce il Form */}
          {registerState.response ? (
            <Result
              status="success"
              title={t(
                'user.registration.response.success.title',
                'Registrazione effettuata con successo!'
              )}
              subTitle={`${t(
                'user.registration.response.success.sub-title',
                `Per attivare l'utente segui le istruzioni che ti abbiamo inviato via email all'indirizzo`
              )} ${registerState.response.data.citizen?.email}.`}
            />
          ) : (
            <Formik<ICitizenRegisterDto>
              enableReinitialize
              initialValues={{} as any}
              validate={schemaToValidator(citizenRegisterValidator)}
              validateOnChange={false}
              onSubmit={async rawValues => {
                try {
                  const values = await citizenRegisterValidator.validate(
                    rawValues,
                    { abortEarly: false }
                  );
                  await register({
                    data: { input: { ...values } }
                  });
                  message.success(
                    t(
                      'user.registration.response.success.title',
                      'Registrazione effettuata con successo!'
                    )
                  );
                } catch (e) {
                  logger.error(
                    t(
                      'user.registration.response.error.title',
                      'Non è stato possibile registarsi.'
                    ),
                    e
                  );
                  message.error(
                    t(
                      'user.registration.response.error.message',
                      'Si è verificato un errore durante la registrazione'
                    )
                  );
                }
              }}
            >
              {({ isSubmitting, dirty }) => (
                <>
                  <PreventNavigation
                    when={dirty && !isSubmitting}
                    message={t(
                      'user.registration.prevent-navigation-message',
                      `Ci sono dati non salvati, abbandonare la pagina? (Tutte le modifiche non salvate andranno perse)`
                    )}
                  />

                  <FormikForm {...layout}>
                    <UserRegistrationForm
                      layout={layout}
                      tailLayout={tailLayout}
                    />
                  </FormikForm>
                </>
              )}
            </Formik>
          )}
        </Card>
      </div>
    </FrontLayout>
  );
}
